
import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { UserHoverCard } from '@/components/ui/user-hover-card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { SkeletonComment } from '@/components/ui/skeleton';
import { CommentItem } from '@/components/ui/comment-item';
import { MentionInput } from '@/components/ui/mention-input';
import { Send, RefreshCw, MessageCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useCommentsStore } from '@/lib/stores/comments';
import { useAuthStore } from '@/lib/stores/auth';
import { useDurableObjectChannel } from '@/hooks/use-durable-object-channel';

interface CommentSectionProps {
  streamId: number;
  isLive?: boolean;
  pollingInterval?: number; // milliseconds, default 5000 for live streams
}

export function CommentSection({ streamId, isLive = false, pollingInterval = 5000 }: CommentSectionProps) {
  const {
    currentStreamComments: comments,  // Use stream-specific comments, not general comments array
    isLoading,
    hasFetchedInitially,
    error,
    fetchStreamComments,
    createComment,
    updateComment,
    deleteComment,
    reportComment,
    likeComment,
    resetComments,
    handleRealtimeComment,
    clearError,
  } = useCommentsStore();

  const { user } = useAuthStore();
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingId, setIsSubmittingId] = useState<number | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(isLive);
  const [retryCount, setRetryCount] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastSubmitTimeRef = useRef<number>(0);
  const submittedIdsRef = useRef<Set<number>>(new Set());

  // Reset comments when stream changes to ensure fresh loading state
  useEffect(() => {
    resetComments();
    fetchStreamComments(streamId);
  }, [streamId, fetchStreamComments, resetComments]);

  // Real-time WebSocket subscription for new comments (only for live streams)
  const handleCommentMessage = useCallback((message: any) => {
    if (message.type === 'new_comment' && message.comment) {
      // Add the comment directly — avoids a full re-fetch that could race with
      // optimistic updates. handleRealtimeComment deduplicates by comment.id.
      handleRealtimeComment(message.comment);
    }
  }, [handleRealtimeComment]);

  const { isConnected } = useDurableObjectChannel({
    channel: `stream:comments:${streamId}`,
    enabled: isSubscribed && isLive,
    onMessage: handleCommentMessage,
  });

  // Update subscription when isLive changes
  useEffect(() => {
    setIsSubscribed(isLive);
  }, [isLive]);

  // Auto-scroll to bottom when new comments arrive (only if already at bottom)
  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      const { scrollHeight, scrollTop, clientHeight } = scrollRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      if (isNearBottom) {
        scrollRef.current.scrollTop = scrollHeight;
      }
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [comments.length, scrollToBottom]);

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    const now = Date.now();
    if (now - lastSubmitTimeRef.current < 2000) {
      toast.warning('Please wait a moment before posting again');
      return;
    }

    lastSubmitTimeRef.current = now;
    setIsSubmitting(true);
    const submittedText = newComment.trim();
    setNewComment('');
    try {
      const newCommentObj = await createComment(streamId, submittedText);
      submittedIdsRef.current.add(newCommentObj.id);
      setTimeout(() => {
        submittedIdsRef.current.delete(newCommentObj.id);
      }, 10000);
      // Scroll to bottom after posting
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      }, 100);
    } catch (error) {
      setNewComment(submittedText);
      toast.error(error instanceof Error ? error.message : 'Failed to post comment. Please try again.');
      console.error('Failed to post comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = () => {
    setRetryCount(r => r + 1);
    clearError();
    fetchStreamComments(streamId);
  };

  const handleLikeComment = async (commentId: string) => {
    try {
      await likeComment(parseInt(commentId));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to like comment.');
      console.error('Failed to like comment:', error);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await deleteComment(parseInt(commentId));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete comment.');
      console.error('Failed to delete comment:', error);
    }
  };

  const handleEditComment = async (commentId: string, content: string) => {
    try {
      await updateComment(parseInt(commentId), content);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update comment.');
      console.error('Failed to update comment:', error);
    }
  };

  const handleReportComment = async (commentId: string, reason: string) => {
    try {
      await reportComment(parseInt(commentId), reason);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to report comment.');
      console.error('Failed to report comment:', error);
    }
  };

  const handleModerateComment = async (commentId: string, action: 'approve' | 'reject') => {
    try {
      await useCommentsStore.getState().moderateComment(parseInt(commentId), action === 'approve');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to moderate comment.');
      console.error('Failed to moderate comment:', error);
    }
  };

  const toggleSubscription = () => {
    setIsSubscribed(!isSubscribed);
  };

  return (
    <div className="flex flex-col w-full min-h-[300px] sm:min-h-[400px] md:min-h-[500px] max-h-[70vh]">
      {/* Real-time subscription indicator */}
      {isLive && (
        <div className="px-4 py-2 border-b flex items-center justify-between text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            {isConnected && isSubscribed ? (
              <>
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                Live updating
              </>
            ) : isSubscribed ? (
              <>
                <RefreshCw className="h-3 w-3 animate-spin" />
                Reconnecting...
              </>
            ) : (
              <>
                <AlertCircle className="h-3 w-3" />
                Updates paused
              </>
            )}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleSubscription}
            className="h-6 px-2"
          >
            {isSubscribed ? (
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                Live
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <RefreshCw className="h-3 w-3" />
                Enable
              </span>
            )}
          </Button>
        </div>
      )}

      {/* Comments List */}
      <ScrollArea className="flex-1 p-4">
        {!hasFetchedInitially && isLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <SkeletonComment key={i} />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-sm text-red-500 mb-2">Failed to load comments</p>
            <p className="text-xs text-muted-foreground mb-4">{error}</p>
            <Button variant="outline" size="sm" onClick={handleRetry}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry {retryCount > 0 && `(${retryCount})`}
            </Button>
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No comments yet</p>
            <p className="text-xs">Be the first to share your thoughts!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {comments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                onLike={() => handleLikeComment(comment.id.toString())}
                onDelete={handleDeleteComment}
                onEdit={handleEditComment}
                onReport={handleReportComment}
                onModerate={handleModerateComment}
                currentUserId={user?.id?.toString()}
                currentUserRole={user?.role?.name}
              />
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Comment Input */}
      <div className="border-t p-4 flex-shrink-0">
        <form onSubmit={handleSubmitComment} className="flex gap-2">
          <UserHoverCard user={{ name: user?.name || 'You', profilePictureUrl: user?.profilePictureUrl || undefined }}>
            <Avatar className="h-8 w-8 shrink-0">
              <AvatarImage src={user?.profilePictureUrl || undefined} />
              <AvatarFallback className="text-xs">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
          </UserHoverCard>
          <div className="flex-1 flex gap-2">
            <MentionInput
              value={newComment}
              onChange={setNewComment}
              placeholder="Share your thoughts... @ to mention"
              disabled={isSubmitting}
              className="flex-1"
              onSubmit={() => {
                if (newComment.trim()) {
                  handleSubmitComment(new Event('submit') as unknown as React.FormEvent);
                }
              }}
            />
            <Button
              type="submit"
              size="sm"
              disabled={!newComment.trim() || isSubmitting}
              className="hover:scale-105 transition-transform duration-150 disabled:hover:scale-100"
            >
              {isSubmitting ? (
                <LoadingSpinner size="sm" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
